import { useState } from 'react';
import { Plus, Send } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Avatar } from '../../components/Avatar';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { PosRoleSelector } from '../../components/PosRoleSelector';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { InviteLinkResult } from '../../components/InviteLinkResult';
import { CreateOrInviteToggle, AccountCreatedPanel } from '../../components/CreateOrInvite';
import { useActiveBusiness, useTeam } from '../../api/hooks';
import { apiPost, USE_MOCKS, mockDelay } from '../../api/client';
import { useQueryClient } from '@tanstack/react-query';
import { formatDate } from '../../lib/format';

const ROLE_LABEL: Record<string, string> = {
  owner: 'Owner', admin: 'Admin', manager: 'Manager', staff: 'Staff', cashier: 'Cashier', readonly: 'Read-only',
};

/**
 * Team members — §5.15
 * Owner manages who can use this business's POS. Scoped to the active business
 * (member RLS): the API returns only this business's members.
 */
export function Team() {
  const [inviteOpen, setInviteOpen] = useState(false);
  const { data: business } = useActiveBusiness();
  const { data: team = [] } = useTeam(business?.id ?? '');

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-5xl mx-auto">
      <PageHeader
        eyebrow={business?.name ?? 'Team'}
        title="Team"
        subtitle="Anyone who needs the POS for this business."
        action={
          <Button
            iconLeft={<Plus className="w-4 h-4" />}
            onClick={() => setInviteOpen(true)}
          >
            Invite team
          </Button>
        }
      />

      {team.length === 0 ? (
        <Card>
          <EmptyState
            title="It's just you here"
            description="Invite your cashier or manager to start using the POS together."
            action={
              <Button
                iconLeft={<Plus className="w-4 h-4" />}
                onClick={() => setInviteOpen(true)}
              >
                Invite team
              </Button>
            }
          />
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto nice-scroll">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50 border-b border-zinc-200 dark:border-zinc-700">
                <tr>
                  <th className="text-left font-medium px-5 sm:px-6 py-3">Member</th>
                  <th className="text-left font-medium px-4 py-3">Role</th>
                  <th className="text-left font-medium px-4 py-3 hidden md:table-cell">
                    Joined
                  </th>
                  <th className="text-left font-medium px-5 sm:px-6 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
                {team.map((m) => (
                  <tr
                    key={m.id}
                    className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition"
                  >
                    <td className="px-5 sm:px-6 py-4">
                      <div className="flex items-center gap-3">
                        <Avatar name={m.name} size="md" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-zinc-950 dark:text-zinc-50 truncate">
                              {m.name}
                            </span>
                            {m.role === 'owner' && (
                              <span className="px-1.5 py-px rounded text-[10px] font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300">
                                Owner
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-zinc-500 dark:text-zinc-400 mono truncate">
                            {m.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-zinc-700 dark:text-zinc-200">{ROLE_LABEL[m.role] ?? m.role}</td>
                    <td className="px-4 py-4 text-zinc-600 dark:text-zinc-300 hidden md:table-cell tabular-nums whitespace-nowrap">
                      {m.joinedAt ? formatDate(m.joinedAt) : <span className="text-zinc-400">—</span>}
                    </td>
                    <td className="px-5 sm:px-6 py-4">
                      {m.status === 'active' && <Badge tone="success">Active</Badge>}
                      {m.status === 'invited' && <Badge tone="warning">Invited</Badge>}
                      {m.status === 'disabled' && <Badge tone="error">Disabled</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <p className="mt-6 text-sm text-zinc-500 dark:text-zinc-400">
        Cashiers and managers can sign in to the POS but never see Subscription, Billing, or
        Team — those routes are limited to Admin.
      </p>

      <InviteTeamModal open={inviteOpen} onClose={() => setInviteOpen(false)} businessId={business?.id ?? ''} />
    </div>
  );
}

type TeamResult =
  | { ok: boolean; inviteUrl: string; email: string; expiresAt?: string }
  | { ok: boolean; action: 'create'; userId: number; email: string; created: boolean; passwordSet: boolean; posRole?: string };

function InviteTeamModal({ open, onClose, businessId }: { open: boolean; onClose: () => void; businessId: string }) {
  const qc = useQueryClient();
  const [action, setAction] = useState<'create' | 'invite'>('invite');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<string>('Cashier');
  const [password, setPassword] = useState('');
  const [allLocations, setAllLocations] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TeamResult | null>(null);

  function close() {
    setAction('invite');
    setEmail('');
    setName('');
    setRole('Cashier');
    setPassword('');
    setAllLocations(true);
    setBusy(false);
    setError(null);
    setResult(null);
    onClose();
  }

  async function submit() {
    if (!email.trim() || !businessId) {
      setError('A valid email is required.');
      return;
    }
    if (action === 'create' && password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = {
        action,
        email: email.trim(),
        name: name.trim() || undefined,
        posRole: role,
        password: action === 'create' ? password : undefined,
      };
      const res: TeamResult = USE_MOCKS
        ? action === 'create'
          ? await mockDelay<TeamResult>({ ok: true, action: 'create', userId: 1, email: body.email, created: true, passwordSet: true, posRole: role })
          : await mockDelay<TeamResult>({ ok: true, inviteUrl: `${window.location.origin}/accept-invite?token=mock-token`, email: body.email })
        : await apiPost<TeamResult>(`/businesses/${businessId}/team`, body);
      setResult(res);
      void qc.invalidateQueries({ queryKey: ['team', businessId] });
    } catch (e) {
      const status = (e as { status?: number })?.status;
      setError(
        status === 409
          ? 'That person is already on the team.'
          : status === 403
            ? 'Only an owner or admin can invite.'
            : action === 'create'
              ? 'Could not create the account.'
              : 'Could not send the invite.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={result ? ('inviteUrl' in result ? 'Invitation sent' : 'Account created') : 'Add team member'}
      size="md"
      footer={
        result ? (
          <Button onClick={close}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={busy}
              loading={busy}
              iconLeft={action === 'create' ? undefined : <Send className="w-4 h-4" />}
            >
              {action === 'create' ? 'Create account' : 'Send invite'}
            </Button>
          </>
        )
      }
    >
      {result ? (
        'inviteUrl' in result ? (
          <InviteLinkResult email={result.email} inviteUrl={result.inviteUrl} />
        ) : (
          <AccountCreatedPanel email={result.email} />
        )
      ) : (
      <div className="space-y-5">
        {error && (
          <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        <CreateOrInviteToggle action={action} onChange={setAction} />
        <Input
          label="Email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="cashier@example.com"
        />
        <Input
          label="Full name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Rina Akter"
        />
        {action === 'create' && (
          <Input
            label="Password"
            type="password"
            required
            hint="Minimum 8 characters"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        )}

        <PosRoleSelector businessId={businessId} value={role} onChange={setRole} />

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
            Locations
          </span>
          <div className="space-y-2">
            <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 px-3 py-2 rounded-md border border-zinc-200 dark:border-zinc-600 cursor-pointer">
              <input
                type="checkbox"
                checked={allLocations}
                onChange={(e) => setAllLocations(e.target.checked)}
                className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
              />
              All locations
            </label>
          </div>
        </div>
      </div>
      )}
    </Modal>
  );
}
